// Load: who is carrying how much team work.
//
// Team deliverables only. Exams and individual projects are excluded on purpose —
// nobody can take your midterm for you, so counting them would hide real imbalance.

import { CLASSES, EFFORT } from "../data/classes.js";
import { PEOPLE } from "../data/people.js";
import { addDays, anchorFriday, d, today } from "../lib/dates.js";
import { byId, el, esc } from "../lib/dom.js";
import { loadShares } from "../lib/items.js";
import { S } from "../lib/state.js";

const SCOPES = [
  ["window", "Next 3 weeks"],
  ["semester", "Whole semester"],
  ["remaining", "Everything still open"],
];

export function render(items, rerender) {
  const filters = byId("loadFilters");
  filters.innerHTML = "";
  SCOPES.forEach(([key, label]) => {
    const b = el("button", "chip", label);
    b.setAttribute("aria-pressed", S.loadScope === key ? "true" : "false");
    b.onclick = () => {
      S.loadScope = key;
      rerender();
    };
    filters.appendChild(b);
  });

  const t = today();
  const anchor = anchorFriday(t);
  const end = addDays(anchor, 21);

  const pool = Object.values(items).filter((it) => {
    if (it.kind !== "group") return false;
    if (S.loadScope === "window") {
      const dd = d(it.due);
      return dd >= anchor && dd <= end;
    }
    if (S.loadScope === "remaining") return it.status !== "done";
    return true;
  });

  const byPerson = Object.fromEntries(PEOPLE.map((p) => [p.id, { total: 0, cls: {}, n: 0, leads: 0 }]));
  let unled = 0;
  let unledWeight = 0;

  pool.forEach((it) => {
    const shares = loadShares(it);
    const who = Object.keys(shares);
    if (!who.length) {
      unled++;
      unledWeight += EFFORT[it.effort || "M"];
      return;
    }
    who.forEach((pid) => {
      byPerson[pid].total += shares[pid];
      byPerson[pid].n++;
      byPerson[pid].cls[it.cls] = (byPerson[pid].cls[it.cls] || 0) + shares[pid];
      if (it.lead === pid) byPerson[pid].leads++;
    });
  });

  const values = PEOPLE.map((p) => byPerson[p.id].total);
  const max = Math.max(1, ...values);
  const sum = values.reduce((a, b) => a + b, 0);
  const avg = sum / PEOPLE.length;

  const grid = byId("loadGrid");
  grid.innerHTML = "";

  // ── by person, stacked by class ──
  const p1 = el("div", "panel");
  p1.innerHTML =
    `<h3>Load by person</h3><p class="ph">${pool.length} team deliverable${pool.length === 1 ? "" : "s"} in scope` +
    (unled ? ` · ${unled} with no lead yet (${unledWeight.toFixed(0)} pts unallocated)` : " · all led") +
    `.</p>`;
  const bars = el("div", "bars");
  PEOPLE.forEach((p) => {
    const v = byPerson[p.id].total;
    const hi = sum > 0 && v > avg * 1.4;
    const lo = sum > 0 && v < avg * 0.6;
    const bar = el("div", "bar" + (hi ? " hi" : lo ? " lo" : ""));
    const segments = CLASSES.filter((c) => byPerson[p.id].cls[c.id])
      .map(
        (c) =>
          `<span style="background:var(${c.v});width:${((byPerson[p.id].cls[c.id] / max) * 100).toFixed(1)}%" title="${c.id}: ${byPerson[p.id].cls[c.id].toFixed(1)}"></span>`
      )
      .join("");
    bar.innerHTML =
      `<span class="nm${p.id === S.me ? " me" : ""}">${esc(p.short)}</span>` +
      `<span class="track">${segments}</span>` +
      `<span class="num">${v ? v.toFixed(1) : "—"}</span>`;
    bars.appendChild(bar);
  });
  p1.appendChild(bars);
  p1.appendChild(
    el("div", "legend", CLASSES.map((c) => `<span><i style="background:var(${c.v})"></i>${c.id}</span>`).join(""))
  );
  grid.appendChild(p1);

  // ── by class ──
  const p2 = el("div", "panel");
  p2.innerHTML = `<h3>Where the team load sits</h3><p class="ph">Total effort weight per class, same scope.</p>`;
  const byClass = {};
  pool.forEach((it) => {
    byClass[it.cls] = (byClass[it.cls] || 0) + EFFORT[it.effort || "M"];
  });
  const classMax = Math.max(1, ...CLASSES.map((c) => byClass[c.id] || 0));
  const bars2 = el("div", "bars");
  CLASSES.forEach((c) => {
    const v = byClass[c.id] || 0;
    const bar = el("div", "bar");
    bar.innerHTML =
      `<span class="nm" title="${esc(c.full)}">${c.id}</span>` +
      `<span class="track"><span style="background:var(${c.v});width:${((v / classMax) * 100).toFixed(1)}%"></span></span>` +
      `<span class="num">${v || "—"}</span>`;
    bars2.appendChild(bar);
  });
  p2.appendChild(bars2);
  p2.appendChild(
    el(
      "div",
      "legend",
      '<span style="color:var(--ink-3)">15.900 sits low here by design — its case memos belong to a four-person memo team, not the core six, so they are tracked as individual work.</span>'
    )
  );
  grid.appendChild(p2);

  // ── matrix ──
  const head =
    "<tr><th>Person</th>" +
    CLASSES.map((c) => `<th title="${esc(c.full)}">${c.id}</th>`).join("") +
    "<th>Total</th><th>Lead</th><th>2nd</th></tr>";

  let rows = "";
  PEOPLE.forEach((p) => {
    const row = byPerson[p.id];
    const seconds = row.n - row.leads;
    rows +=
      `<tr><td class="nm"${p.id === S.me ? ' style="color:var(--accent)"' : ""}>${esc(p.short)}</td>` +
      CLASSES.map((c) => {
        const v = row.cls[c.id];
        return `<td class="${v ? "" : "zero"}">${v ? v.toFixed(1) : "·"}</td>`;
      }).join("") +
      `<td style="font-weight:600">${row.total.toFixed(1)}</td>` +
      `<td${row.leads ? ' style="color:var(--brand);font-weight:600"' : ' class="zero"'}>${row.leads || "·"}</td>` +
      `<td class="${seconds ? "" : "zero"}">${seconds || "·"}</td></tr>`;
  });

  const totals = CLASSES.map((c) => {
    const s = PEOPLE.reduce((a, p) => a + (byPerson[p.id].cls[c.id] || 0), 0);
    return `<td>${s ? s.toFixed(1) : "·"}</td>`;
  }).join("");
  const totalLeads = PEOPLE.reduce((a, p) => a + byPerson[p.id].leads, 0);
  const totalAssigned = PEOPLE.reduce((a, p) => a + byPerson[p.id].n, 0);
  rows += `<tr class="total"><td class="nm">Team</td>${totals}<td>${sum.toFixed(1)}</td><td>${totalLeads}</td><td>${totalAssigned - totalLeads}</td></tr>`;

  byId("loadMatrix").innerHTML = head + rows;
}
